import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWebSocket,
  type AssistantResponseEvent,
  type AssistantPendingConfirmationEvent,
} from './useWebSocket';
import { useAssistantStore } from '@/stores/assistantStore';
import {
  failureOf,
  invalidateAssistantWriteCaches,
  mayHaveWrittenTurn,
  settledTurnOf,
} from './assistantOutcome';

/**
 * Subscribes to assistant WebSocket events (`assistant:response`,
 * `assistant:pending_confirmation`) and renders them through the store.
 *
 * The HTTP `/chat`, `/confirm`, `/cancel` responses also call the same store
 * actions with the persisted `messageId`, so messages dedupe naturally — the
 * faster path wins.
 *
 * Mount once at the top of the assistant UI (AssistantPanel).
 */
export function useAssistantRealtime(): void {
  const queryClient = useQueryClient();
  const conversationId = useAssistantStore((s) => s.conversationId);
  const setConversationId = useAssistantStore((s) => s.setConversationId);
  const upsertAssistantMessage = useAssistantStore((s) => s.upsertAssistantMessage);
  const setLoading = useAssistantStore((s) => s.setLoading);

  const onResponse = useCallback(
    (data: AssistantResponseEvent) => {
      if (!conversationId && data.conversationId) {
        setConversationId(data.conversationId);
      }
      // A settled event is read by the same rules as the HTTP reply for the
      // same messageId (`settledTurnOf`), so the two cannot disagree in
      // either arrival order. A plain message keeps its own type.
      const settled =
        data.type === 'action_confirmed' || data.type === 'action_cancelled'
          ? settledTurnOf(data, { type: data.type, actionId: data.actionId ?? '' })
          : undefined;
      const failure = settled ? settled.failure : failureOf(data.failure);
      upsertAssistantMessage({
        id: data.messageId,
        conversationId: data.conversationId,
        role: 'assistant',
        content: data.content,
        createdAt: new Date().toISOString(),
        type: settled?.type ?? data.type,
        actionId: settled?.actionId ?? data.actionId,
        // Only when the event carries one: a legacy turn has none of these,
        // and spreading `undefined` here would clobber a value the HTTP reply
        // had already merged in for the same messageId.
        ...(settled?.outcome ? { outcome: settled.outcome } : {}),
        ...(settled?.presenter ? { presenter: settled.presenter } : {}),
        ...(failure ? { failure } : {}),
        ...(settled?.pinnedReceivedAt ? { pinnedReceivedAt: settled.pinnedReceivedAt } : {}),
        ...(settled?.alreadyCompleted ? { alreadyCompleted: true } : {}),
        ...(settled?.quoteResult ? { quoteResult: settled.quoteResult } : {}),
      });
      setLoading(false);
      // Settled (here, in another tab, or this tab's HTTP reply lost):
      // settlement is final — the card and its correction state are retired
      // and a later pending event for it is ignored (#2821).
      if (settled && settled.actionId) useAssistantStore.getState().markSettled(settled.actionId);

      // A confirmed turn, or a cancel the server marks `outcome: 'unknown'`
      // (#2597) — the same rule the HTTP fallback applies, so a second tab
      // that only sees the socket refreshes too.
      if (settled && mayHaveWrittenTurn(settled.type, settled.outcome)) {
        invalidateAssistantWriteCaches(queryClient);
      }
    },
    [conversationId, setConversationId, upsertAssistantMessage, setLoading, queryClient]
  );

  const onPendingConfirmation = useCallback(
    (data: AssistantPendingConfirmationEvent) => {
      // A delayed pending event for an action already settled must not
      // re-arm its card (#2821); `armPendingConfirmation` below is the guard,
      // this only skips the stale transcript row.
      if (useAssistantStore.getState().settledActionIds.includes(data.actionId)) return;
      if (!conversationId && data.conversationId) {
        setConversationId(data.conversationId);
      }
      upsertAssistantMessage({
        id: data.messageId,
        conversationId: data.conversationId,
        role: 'assistant',
        content: data.description,
        createdAt: new Date().toISOString(),
        type: 'pending_confirmation',
        actionId: data.actionId,
        toolName: data.toolName,
        params: data.params,
        ...(data.presenter ? { presenter: data.presenter } : {}),
      });
      useAssistantStore.getState().armPendingConfirmation({
        actionId: data.actionId,
        toolName: data.toolName,
        description: data.description,
        params: data.params,
        ...(data.presenter ? { presenter: data.presenter } : {}),
      });
      setLoading(false);
    },
    [conversationId, setConversationId, upsertAssistantMessage, setLoading]
  );

  useWebSocket('assistant:response', onResponse);
  useWebSocket('assistant:pending_confirmation', onPendingConfirmation);
}
